import { type FocusEvent, useState } from "react";
import type { TextFieldProps } from "react-aria-components";
import { useDebounceCallback } from "usehooks-ts";

export interface DebounceParams {
    debounceMs?: number;

    value?: string;
    defaultValue?: string;

    onChange?: (value: string) => void;
    onBlur?: (e: FocusEvent<any>) => void;
}

/** @returns props for a `<TextField>` / `<Input>` element */
export function useDebounceInput({ onChange, debounceMs = 300, value, defaultValue, onBlur }: DebounceParams) {
    /** `undefined` means currently not editing */
    const [editingValue, setEditingValue] = useState<string | undefined>();

    const debouncedOnChange = useDebounceCallback(onChange ?? (() => {}), debounceMs);

    function onInputChange(value: string) {
        setEditingValue(value);
        debouncedOnChange(value);
    }

    const realValue = editingValue ?? value ?? defaultValue ?? "";

    return {
        value: realValue,
        onChange: onInputChange,
        onBlur: ((e) => {
            setEditingValue(undefined);
            onBlur?.(e);
        }) satisfies DebounceParams["onBlur"],
    } satisfies TextFieldProps;
}
